import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, BookOpen, Library, Search, Store, Upload, User } from 'lucide-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { t } from '../i18n';
import LibraryBookCard, { learningLanguageLabel } from './LibraryBookCard';
import LibraryBrowseGroupCard from './LibraryBrowseGroupCard';
import { groupLibraryItems } from '../utils/libraryGrouping';
import './LibraryBookshop.css';

const DIFFICULTIES = { 1: 'Beginner', 2: 'Elementary', 3: 'Intermediate', 4: 'Upper-Intermediate', 5: 'Advanced' };
const AUDIENCES = ['ko', 'en', 'ja', 'zh', 'es'];
const LEARNING_LOCALES = ['en-US', 'ko-KR', 'ja-JP', 'zh-CN', 'es-ES'];

function PresetLibraryPage({ market = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const paramKey = params.toString();
  const filters = useMemo(() => {
    const query = new URLSearchParams(paramKey);
    const view = market ? (query.get('view') === 'mine' ? 'mine' : 'market') : query.get('view') || 'discover';
    return {
      view,
      groupBy: query.get('groupBy') || 'difficulty',
      locale: query.get('locale') || 'ko',
      learningLocale: query.get('learningLocale') || 'all',
      difficulty: query.get('difficulty') || 'all', topic: query.get('topic') || 'all',
      source: view === 'all' ? 'official' : query.get('source') || 'all', group: query.get('group') || 'all', sort: query.get('sort') || 'default',
      query: query.get('query') || '', page: query.get('page') || '1', pageSize: '24',
      studyKind: query.get('studyKind') || 'all',
    };
  }, [paramKey, market]);
  const [search, setSearch] = useState(filters.query);
  const [result, setResult] = useState(null);
  const [recommendedIds, setRecommendedIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const discover = filters.view === 'discover';
  const mine = filters.view === 'mine';
  const favoritesView = filters.view === 'favorites';
  const marketView = filters.view === 'market' || mine || favoritesView;
  const marketSection = filters.view === 'market' || mine;
  const activeFilterCount = [
    Boolean(filters.query), filters.learningLocale !== 'all', filters.difficulty !== 'all',
    !marketView && filters.topic !== 'all',
    !mine && filters.locale !== 'ko', filters.group !== 'all', filters.sort !== 'default',
    filters.studyKind !== 'all',
  ].filter(Boolean).length;
  const showTools = !discover && (filtersExpanded || activeFilterCount > 0 || filters.group !== 'all');

  useEffect(() => { setSearch(filters.query); }, [filters.query]);
  useEffect(() => { setFiltersExpanded(false); }, [filters.view]);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setRecommendedIds(new Set());
    Promise.all([
      apiClient.getLibraryBrowse(filters),
      filters.view === 'all' && filters.source === 'official' && filters.studyKind !== 'vocabulary'
        ? Promise.resolve(apiClient.getStudyPath(filters.locale)).catch(() => null)
        : Promise.resolve(null),
    ]).then(([data, studyPath]) => {
      if (!cancelled) {
        setResult(data);
        setRecommendedIds(new Set((studyPath?.recommendations || []).map((item) => item.id)));
      }
    }).catch((requestError) => {
      if (!cancelled) setError(requestError.message || t('library.loadError'));
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, retry]);

  const change = (patch, targetView = discover ? 'all' : filters.view) => {
    const next = { ...filters, page: '1', ...patch, view: targetView };
    if (targetView === 'discover') {
      Object.assign(next, { query: '', difficulty: 'all', topic: 'all', source: 'all', group: 'all', sort: 'default' });
    }
    if (targetView === 'market' || targetView === 'mine' || targetView === 'favorites') {
      next.source = 'all';
      next.topic = 'all';
      next.group = 'all';
    }
    if (targetView === 'all') next.source = 'official';
    if (targetView === 'all' && !['default', 'rating'].includes(next.sort)) next.sort = 'default';
    const groupOptions = ['market', 'mine', 'favorites'].includes(targetView) ? ['difficulty', 'language'] : ['difficulty', 'topic'];
    if (targetView === 'discover' || !groupOptions.includes(next.groupBy)) next.groupBy = 'difficulty';
    const query = new URLSearchParams();
    Object.entries(next).forEach(([key, value]) => {
      if (key !== 'pageSize' && value && (key === 'view' || value !== 'all') && value !== 'default' && !(key === 'page' && value === '1')) query.set(key, value);
    });
    navigate(`${['market', 'mine'].includes(targetView) ? '/market' : '/library'}?${query}`);
  };
  const reset = () => change({ query: '', difficulty: 'all', topic: 'all', source: 'all', group: 'all', sort: 'default', learningLocale: 'all', groupBy: 'difficulty', studyKind: 'all' });
  const openItem = (item) => navigate(`/${item.source === 'market' ? 'market' : 'library'}/${encodeURIComponent(item.id)}`, {
    state: { libraryReturnTo: `${location.pathname}${location.search}`, ...(filters.studyKind === 'vocabulary' ? { vocabularyReturnTo: '/vocabulary' } : {}) },
  });
  const selectedNoteId = params.get('selectedNoteId');
  const withRecommendationBadge = (item) => {
    const selectedRecommendation = Boolean(selectedNoteId && item.id === selectedNoteId);
    return recommendedIds.has(item.id) || selectedRecommendation
      ? { ...item, recommended: true, selectedRecommendation }
      : item;
  };
  const grid = (items) => <div className="library-book-grid">{items.map((item) => (
    <LibraryBookCard key={`${item.source}:${item.id}`} item={withRecommendationBadge(item)} onOpen={openItem} />
  ))}</div>;
  const list = (items) => <div className="library-book-list">{items.map((item) => (
    <LibraryBookCard key={`${item.source}:${item.id}`} item={withRecommendationBadge(item)} onOpen={openItem} layout="list" />
  ))}</div>;
  const openBrowseGroup = (group) => {
    if (group.source === 'favorites') {
      change({ query: '', source: 'all', topic: 'all', group: 'all', sort: 'default' }, 'favorites');
      return;
    }
    if (group.source === 'market') {
      change({ group: 'all', source: 'all', topic: 'all' }, 'market');
      return;
    }
    change({ group: group.id, source: 'official', topic: 'all' }, 'all');
  };
  const selectedGroupTitle = result?.groupTitle || filters.group;
  const browseGroups = Array.isArray(result?.groups) ? result.groups : [];
  const hasCuratedGroups = browseGroups.some((group) => group.source !== 'favorites' && group.id !== 'editor-picks');
  const groupTitle = (value) => {
    if (filters.groupBy === 'topic') return value || t('library.unclassified');
    if (filters.groupBy === 'language') return value ? learningLanguageLabel(value) : t('library.unknownLanguage');
    return DIFFICULTIES[value]
      ? `${t('library.level', undefined, { level: value })} · ${t(`library.difficulties.${value}`)}`
      : t('library.unknownDifficulty');
  };

  return (
    <div className="library-bookshop">
      <header className="library-heading app-page-header">
        <div className="library-heading-copy app-page-header-copy"><h1>{t('library.title')}</h1><p>{t('library.subtitle')}</p></div>
        <div className="library-heading-actions app-page-header-actions">
          {marketSection && <div className="library-owner-actions">
            <button type="button" aria-label={t('library.myUploads')} title={t('library.myUploads')} aria-pressed={mine} onClick={() => change({ query: '', learningLocale: 'all', difficulty: 'all' }, 'mine')}><User size={22} aria-hidden="true" /></button>
            <button type="button" aria-label={t('library.publish')} title={t('library.publish')} onClick={() => navigate(filters.studyKind === 'vocabulary' ? '/market/publish?studyKind=vocabulary' : '/market/publish')}><Upload size={22} aria-hidden="true" /></button>
          </div>
          }
          {!discover && <button
            type="button"
            className={`library-search-toggle${filtersExpanded ? ' is-open' : ''}`}
            aria-expanded={filtersExpanded}
            aria-controls="library-filter-panel"
            aria-label={t(filtersExpanded ? 'library.closeSearchFilters' : 'library.openSearchFilters')}
            title={t(filtersExpanded ? 'library.closeSearchFilters' : 'library.openSearchFilters')}
            onClick={() => setFiltersExpanded((expanded) => !expanded)}
          >
            <Search size={22} aria-hidden="true" />
            {activeFilterCount > 0 && <span className="library-search-toggle-count" aria-hidden="true">{activeFilterCount}</span>}
          </button>}
        </div>
      </header>

      <nav className="library-navigation" aria-label={t('library.navigation')}>
        <button type="button" aria-current={discover || favoritesView ? 'page' : undefined} onClick={() => change({}, 'discover')}><BookOpen size={18} aria-hidden="true" />{t('library.discover')}</button>
        <button type="button" aria-current={filters.view === 'all' ? 'page' : undefined} onClick={() => change({ source: 'official', group: 'all' }, 'all')}><Library size={18} aria-hidden="true" />{t('library.officialTab')}</button>
        <button type="button" aria-current={marketSection ? 'page' : undefined} onClick={() => change({}, 'market')}><Store size={18} aria-hidden="true" />{t('library.market')}</button>
      </nav>

      {!discover && <section className="library-tools" aria-label={t('library.filters')} hidden={!showTools}>
        <div id="library-filter-panel" className="library-filter-content" hidden={!filtersExpanded}>
        <div className="library-language-row">
          {!mine && <label>{t('library.audience')}<select value={filters.locale} onChange={(event) => change({ locale: event.target.value, topic: 'all', group: 'all' }, filters.view)}>
            {AUDIENCES.map((code) => <option key={code} value={code}>{t(`library.languages.${code}`)}</option>)}
          </select></label>}
          <label>{t('library.learningLanguage')}<select value={filters.learningLocale} onChange={(event) => change({ learningLocale: event.target.value }, filters.view)}>
            <option value="all">{t('library.allLanguages')}</option>
            {LEARNING_LOCALES.map((code) => <option key={code} value={code}>{learningLanguageLabel(code)}</option>)}
          </select></label>
          <form className="library-search" onSubmit={(event) => { event.preventDefault(); change({ query: search.trim() }); }}>
            <Search size={18} aria-hidden="true" />
            <input type="search" maxLength={200} aria-label={t('library.search')} placeholder={t('library.search')} value={search} onChange={(event) => setSearch(event.target.value)} />
            <button type="submit">{t('library.searchButton')}</button>
          </form>
        </div>
        <div className="library-filter-row">
          <label>{t('vocabulary.studyKind')}<select value={filters.studyKind} onChange={(event) => change({ studyKind: event.target.value, group: 'all' })}>
            <option value="all">{t('vocabulary.allKinds')}</option><option value="standard">{t('vocabulary.standardType')}</option><option value="vocabulary">{t('vocabulary.noteType')}</option>
          </select></label>
          <label>{t('library.difficulty')}<select value={filters.difficulty} onChange={(event) => change({ difficulty: event.target.value })}>
            <option value="all">{t('library.allLevels')}</option>
            {Object.keys(DIFFICULTIES).map((level) => <option key={level} value={level}>{level}. {t(`library.difficulties.${level}`)}</option>)}
          </select></label>
          {!marketView && <>
            <label>{t('library.topic')}<select value={filters.topic} onChange={(event) => change({ topic: event.target.value })}>
              <option value="all">{t('library.allTopics')}</option>
              {(result?.topics || []).map((topic) => <option key={topic} value={topic}>{topic}</option>)}
              <option value="unclassified">{t('library.unclassified')}</option>
            </select></label>
          </>}
          <label>{t('library.sort')}<select value={filters.sort} onChange={(event) => change({ sort: event.target.value })}>
            <option value="default">{t(marketView ? 'library.latest' : 'library.defaultSort')}</option>
            <option value="rating">{t('library.ratingSort')}</option>
            {marketView && <option value="imports">{t('library.importSort')}</option>}
          </select></label>
        </div>
        <div className="library-grouping" role="group" aria-label={t('library.groupBy')}>
          <span>{t('library.groupBy')}</span>
          <button type="button" aria-pressed={filters.groupBy === 'difficulty'} onClick={() => change({ groupBy: 'difficulty' })}>{t('library.difficulty')}</button>
          <button type="button" aria-pressed={filters.groupBy === (marketView ? 'language' : 'topic')}
            onClick={() => change({ groupBy: marketView ? 'language' : 'topic' })}>{t(marketView ? 'library.learningLanguage' : 'library.topic')}</button>
        </div>
        </div>
        {activeFilterCount > 0 && <div className="library-filter-summary">
          <span className="library-active-filters">{t('library.activeFilters', undefined, { count: activeFilterCount })}</span>
          <button type="button" className="library-clear" onClick={reset}>{t('library.clearFilters')}</button>
        </div>}
        {filters.group !== 'all' && <div className="library-selected-group">
          <span>{t('library.selectedGroup', undefined, { group: selectedGroupTitle })}</span>
          <button type="button" onClick={() => change({ group: 'all' })}>{t('library.clearGroup')}</button>
        </div>}
      </section>}

      <div aria-live="polite" aria-busy={loading}>
        {loading && <div className="library-state" role="status">{t('library.loading')}</div>}
        {!loading && error && <div className="library-state" role="alert"><p>{error}</p><button type="button" onClick={() => setRetry((value) => value + 1)}>{t('library.retry')}</button><button type="button" onClick={() => navigate('/library')}>{t('library.clearFilters')}</button></div>}
        {!loading && !error && result && <>
          {!result.total && <div className="library-state"><BookOpen size={32} aria-hidden="true" /><p>{t('library.empty')}</p><button type="button" onClick={reset}>{t('library.clearFilters')}</button></div>}
          {discover ? <div className="library-discovery">
            {browseGroups.length > 0 ? <div className="library-discovery-groups">
              {browseGroups.map((group) => <LibraryBrowseGroupCard
                key={`${group.source}:${group.id}`}
                group={group}
                locale={filters.locale}
                onOpen={openBrowseGroup}
              />)}
            </div> : null}
            {!hasCuratedGroups && (result.shelves || []).filter((shelf) => shelf.items.length > 0).map((shelf) => (
              <section className={`library-shelf is-${shelf.source}`} key={shelf.source} aria-label={t(`library.${shelf.source}Shelf`)}>
                <header><div><span className="library-eyebrow">{t(shelf.source === 'official' ? 'library.official' : 'library.community')}</span><h2>{t(`library.${shelf.source}Shelf`)}</h2><p>{t(`library.${shelf.source}Intro`)}</p></div>
                  <button type="button" onClick={() => change({ source: shelf.source }, shelf.source === 'market' ? 'market' : 'all')}>{t('library.viewAll')} <ArrowRight size={16} aria-hidden="true" /></button>
                </header>
                {grid(shelf.items)}
              </section>
            ))}
            <button type="button" className="library-explore-all" onClick={() => change({ source: 'all', group: 'all' }, 'all')}>{t('library.exploreAll')}<ArrowRight size={18} aria-hidden="true" /></button>
          </div> : <section className="library-results" aria-label={t('library.results')}>
            {mine && <p className="library-management-hint">{t('library.managementHint')}</p>}
            {result.resultsLimited && <p className="library-management-hint">{t('library.limitedResults')}</p>}
            <div className="library-result-groups">
              {groupLibraryItems(result.items || [], filters.groupBy).map((group) => (
                <section className="library-result-group" key={group.value} aria-label={groupTitle(group.value)}>
                  <header><h3>{groupTitle(group.value)}</h3><span aria-label={t('library.pageGroupCount', undefined, { count: group.items.length })}>{group.items.length}</span></header>
                  {list(group.items)}
                </section>
              ))}
            </div>
            {result.pageCount > 1 && <nav className="library-pagination" aria-label={t('library.pagination')}>
              <button type="button" disabled={result.page <= 1} onClick={() => change({ page: String(result.page - 1) })}>{t('library.previous')}</button>
              <span>{result.page} / {result.pageCount}</span>
              <button type="button" disabled={result.page >= result.pageCount} onClick={() => change({ page: String(result.page + 1) })}>{t('library.next')}</button>
            </nav>}
          </section>}
        </>}
      </div>
      <footer className="library-license">Images used under license from Shutterstock.com.</footer>
    </div>
  );
}

export { DIFFICULTIES };
export default PresetLibraryPage;
